import React, { useState, useRef, useCallback } from 'react';
import CohortItem from './CohortItem';

export default function CohortMultiSelect({ 
  cohorts = [], 
  selectedCohorts = [], 
  onChangeCohortList, 
  isProcessing = false, 
  processingMessage = '',
  placeholder = 'Select cohorts...'
}) {
  const cohortInputRef = useRef(null);

  const [cohortList, setCohortList] = useState(selectedCohorts || []);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter cohorts based on search term and exclude already selected ones
  const filteredCohorts = cohorts.filter(cohort => {
    const isNotSelected = !cohortList.some(selected => selected.value === cohort.value);
    const matchesSearch = cohort.label.toLowerCase().includes(searchTerm.toLowerCase());
    return isNotSelected && matchesSearch;
  });

  const handleCohortSelect = (cohort) => {
    const updatedCohortList = [...cohortList, cohort];
    setCohortList(updatedCohortList);
    onChangeCohortList(updatedCohortList);
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  const removeCohort = useCallback((cohortToRemove) => {
    const filteredList = cohortList.filter(
      (cohort) => cohort.value !== cohortToRemove.value
    );
    setCohortList(filteredList);
    onChangeCohortList(filteredList);
  }, [cohortList, onChangeCohortList]);

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
    setIsDropdownOpen(true);
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleInputBlur = () => {
    // Delay closing to allow for click events on dropdown items
    setTimeout(() => setIsDropdownOpen(false), 150);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsDropdownOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-row gap-2 mb-2">
        <p className="text-sm text-charcoal font-bold">Cohorts</p>
      </div>
      
      <div
        className={`cohort-list w-full p-4 flex flex-wrap border-2 border-gray rounded-lg relative ${
          isProcessing ? 'opacity-50 pointer-events-none' : ''
        }`}
        onClick={() => !isProcessing && cohortInputRef.current?.focus()}
      >
        {isProcessing && (
          <p className="text-gray absolute pin-t pin-l m-4 ml-5">
            {processingMessage || 'Processing cohorts...'}
          </p>
        )}
        {cohortList.map((cohort) => (
          <CohortItem 
            key={cohort.value} 
            cohort={cohort} 
            onRemove={isProcessing ? () => {} : removeCohort} 
          />
        ))}
        {!isProcessing && (
          <input
            ref={cohortInputRef}
            type="text"
            className="cohort-input outline-none bg-transparent border-none"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            placeholder={cohortList.length === 0 ? placeholder : ''}
          />
        )}
      </div>

      {/* Dropdown */}
      {isDropdownOpen && filteredCohorts.length > 0 && !isProcessing && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredCohorts.map((cohort) => (
            <div
              key={cohort.value}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
              onClick={() => handleCohortSelect(cohort)}
            >
              {cohort.label}
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {isDropdownOpen && filteredCohorts.length === 0 && searchTerm && !isProcessing && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="px-4 py-2 text-gray-500">
            No cohorts found matching "{searchTerm}"
          </div>
        </div>
      )}
    </div>
  );
}
